import { useQuery } from "@tanstack/react-query";
import { useUser, useOrganization } from "@clerk/clerk-react";
import { useToast } from "@/hooks/use-toast";
import { toastApiError } from "@/lib/error-messages";
import type { QuoteTemplate, CreateQuoteTemplateInput } from "@onsemble/shared";
import { getQuoteTemplates } from "@/requests/quote-templates/get-quote-templates";
import { useCreateQuoteTemplate } from "@/requests/quote-templates/create-quote-template";

interface UseTemplatesResult {
  templates: QuoteTemplate[];
  isLoading: boolean;
  error: unknown;
  createTemplate: (data: CreateQuoteTemplateInput) => Promise<QuoteTemplate>;
  isCreating: boolean;
}

export const useTemplates = (): UseTemplatesResult => {
  const { user } = useUser();
  const { organization } = useOrganization();
  const { toast } = useToast();

  // Use the new typed get quote templates query
  const { data, isLoading, error } = useQuery({
    ...getQuoteTemplates(),
    enabled: !!user && !!organization,
  });

  // Use the new typed create quote template hook
  const createQuoteTemplateMutation = useCreateQuoteTemplate();

  const createTemplate = async (
    data: CreateQuoteTemplateInput,
  ): Promise<QuoteTemplate> => {
    if (!organization?.id) {
      throw new Error("Organization data not available");
    }

    try {
      const template = await createQuoteTemplateMutation.mutateAsync(data);
      toast({
        title: "Template created successfully",
      });
      return template;
    } catch (error) {
      toastApiError(toast, error, "Failed to create template");
      throw error;
    }
  };

  return {
    templates: data || [],
    isLoading,
    error,
    createTemplate,
    isCreating: createQuoteTemplateMutation.isPending,
  };
};

export default useTemplates;
